import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { PLAN_CONFIG, type PlanKey } from "@/lib/plans";
import { EmbeddedCheckoutDialog } from "@/components/EmbeddedCheckoutDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useFamily } from "@/hooks/useFamily";

const EMOJI_OPTIONS = ["🧒", "👧", "👦", "🧒🏽", "👧🏿", "👦🏼", "🐱", "🦊", "🐶", "🦄", "🐻", "🐰"];

interface ChildEntry {
  name: string;
  emoji: string;
}

export default function Onboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const planParam = searchParams.get("plan") as PlanKey | null;
  const isInvited = searchParams.get("invited") === "true";

  const { memberships } = useFamily();

  const [parentFirstName, setParentFirstName] = useState("");
  const [childFacingName, setChildFacingName] = useState("");
  const [children, setChildren] = useState<ChildEntry[]>([{ name: "", emoji: "🧒" }]);
  const [loading, setLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutFamilyId, setCheckoutFamilyId] = useState<string>();
  const [openEmojiForIndex, setOpenEmojiForIndex] = useState<number | null>(null);

  // Password for invited users
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const canAddMoreChildren = true;

  function addChild() {
    setChildren((prev) => [...prev, { name: "", emoji: "🧒" }]);
  }

  function updateChild(index: number, field: keyof ChildEntry, value: string) {
    setChildren((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  }

  function removeChild(index: number) {
    if (children.length > 1) {
      setChildren((prev) => prev.filter((_, i) => i !== index));
    }
  }

  /** Invited co-parent: just save display name, then go to dashboard */
  async function handleInvitedSetup() {
    if (!user) return;
    const firstName = parentFirstName.trim();
    if (!firstName) {
      toast.error("Skriv inn ditt fornavn");
      return;
    }
    const whatChildCallsYou = childFacingName.trim();
    if (!whatChildCallsYou) {
      toast.error("Skriv inn hva barnet kaller deg");
      return;
    }
    if (password.length < 8) {
      toast.error("Passordet må ha minst 8 tegn");
      return;
    }
    if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      toast.error("Passordet må inneholde stor bokstav, liten bokstav og tall");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passordene stemmer ikke overens");
      return;
    }

    setLoading(true);
    try {
      // Set password for the invited user
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) throw pwError;

      // Update display_name and child_facing_name on all family_members rows for this user
      const { error } = await supabase
        .from("family_members")
        .update({
          display_name: firstName,
          child_facing_name: whatChildCallsYou,
        })
        .eq("user_id", user.id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["family-memberships"] });
      toast.success("Velkommen!");
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error("Noe gikk galt: " + (err.message || "Ukjent feil"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateFamily() {
    if (!user) return;
    const firstName = parentFirstName.trim();
    if (!firstName) {
      toast.error("Skriv inn ditt fornavn");
      return;
    }
    const whatChildCallsYou = childFacingName.trim();
    if (!whatChildCallsYou) {
      toast.error("Skriv inn hva barnet kaller deg");
      return;
    }
    let validChildren = children.filter((c) => c.name.trim());
    if (validChildren.length === 0) {
      toast.error("Legg til minst ett barn");
      return;
    }

    setLoading(true);
    try {
      // 1. Create family via RPC
      const { data: familyId, error: famErr } = await supabase
        .rpc("create_family", { _home_mode: "shared" });
      if (famErr) throw famErr;
      const family = { id: familyId };

      // 2. Add self as parent
      const { error: memErr } = await supabase
        .from("family_members")
        .insert({
          family_id: family.id,
          user_id: user.id,
          role: "parent",
          display_name: firstName,
          child_facing_name: whatChildCallsYou,
        });
      if (memErr) throw memErr;

      // 3. Add children
      const childInserts = validChildren.map((c) => ({
        family_id: family.id,
        name: c.name.trim(),
        avatar_emoji: c.emoji,
      }));
      const { error: childErr } = await supabase.from("children").insert(childInserts);
      if (childErr) throw childErr;

      // 4. Seed default checklist template
      const { data: template, error: tplErr } = await supabase
        .from("checklist_templates")
        .insert({ family_id: family.id, name: "Standard bytte" })
        .select()
        .single();
      if (tplErr) throw tplErr;

      const defaultItems = [
        "PC/iPad + lader",
        "Ukens bøker / lekser",
        "Gymtøy / turklær",
        "Fritidsutstyr (fotballbag osv.)",
        "Medisin (om relevant)",
        "Innesko / yttertøy etter vær",
      ];
      const itemInserts = defaultItems.map((label, i) => ({
        template_id: template.id,
        label,
        sort_order: i,
      }));
      const { error: itemErr } = await supabase.from("checklist_items").insert(itemInserts);
      if (itemErr) throw itemErr;

      await queryClient.invalidateQueries({ queryKey: ["family-memberships"] });

      if (planParam && planParam in PLAN_CONFIG) {
        setCheckoutFamilyId(family.id);
        setShowCheckout(true);
      }

      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error("Noe gikk galt: " + (err.message || "Ukjent feil"));
    } finally {
      setLoading(false);
    }
  }

  // --- Invited co-parent: simplified onboarding (name only) ---
  if (isInvited) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 text-4xl">👋</div>
            <CardTitle className="text-2xl">Velkommen til Flyt!</CardTitle>
            <CardDescription>
              Fortell oss litt om deg, så er du klar.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="parent-firstname" className="text-sm font-medium">
                Ditt fornavn
              </label>
              <Input
                id="parent-firstname"
                value={parentFirstName}
                onChange={(e) => setParentFirstName(e.target.value)}
                placeholder="F.eks. Erik"
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="child-facing-name" className="text-sm font-medium">
                Hva kaller barnet deg?
              </label>
              <Input
                id="child-facing-name"
                value={childFacingName}
                onChange={(e) => setChildFacingName(e.target.value)}
                placeholder="F.eks. Pappa, Mamma, Far, Mor"
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="invite-password" className="text-sm font-medium">
                Velg et passord
              </label>
              <PasswordInput
                id="invite-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minst 8 tegn"
                autoComplete="new-password"
                minLength={8}
              />
              <ul className="text-xs text-muted-foreground space-y-0.5">
                <li className={password.length >= 8 ? "text-muted-foreground" : "text-destructive"}>
                  {password.length >= 8 ? "✓" : "○"} Minst 8 tegn
                </li>
                <li className={/[a-z]/.test(password) && /[A-Z]/.test(password) ? "text-muted-foreground" : "text-destructive"}>
                  {/[a-z]/.test(password) && /[A-Z]/.test(password) ? "✓" : "○"} Stor og liten bokstav
                </li>
                <li className={/[0-9]/.test(password) ? "text-muted-foreground" : "text-destructive"}>
                  {/[0-9]/.test(password) ? "✓" : "○"} Minst ett tall
                </li>
              </ul>
            </div>
            <div className="space-y-2">
              <label htmlFor="invite-confirm-password" className="text-sm font-medium">
                Bekreft passord
              </label>
              <PasswordInput
                id="invite-confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Skriv passordet på nytt"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <Button onClick={handleInvitedSetup} disabled={loading} className="w-full">
              {loading ? "Lagrer…" : "Kom i gang"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- Normal onboarding: create family + add children ---
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 text-4xl">🧒</div>
          <CardTitle className="text-2xl">Legg til barn</CardTitle>
          <CardDescription>Hvem skal bruke Flyt?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="parent-firstname" className="text-sm font-medium">
              Ditt fornavn
            </label>
            <Input
              id="parent-firstname"
              value={parentFirstName}
              onChange={(e) => setParentFirstName(e.target.value)}
              placeholder="F.eks. Anne"
              autoComplete="given-name"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="child-facing-name" className="text-sm font-medium">
              Hva kaller barnet deg?
            </label>
            <Input
              id="child-facing-name"
              value={childFacingName}
              onChange={(e) => setChildFacingName(e.target.value)}
              placeholder="F.eks. Mamma, Pappa, Far, Mor"
              autoComplete="off"
            />
          </div>
          {children.map((child, i) => (
            <div key={i} className="space-y-2 rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <Input
                  value={child.name}
                  onChange={(e) => updateChild(i, "name", e.target.value)}
                  placeholder="Navn"
                  className="flex-1"
                />
                <Popover
                  open={openEmojiForIndex === i}
                  onOpenChange={(open) => setOpenEmojiForIndex(open ? i : null)}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 shrink-0 text-2xl"
                      aria-label="Velg emoji"
                    >
                      {child.emoji}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2" align="end">
                    <div className="flex flex-wrap gap-1.5">
                      {EMOJI_OPTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => {
                            updateChild(i, "emoji", emoji);
                            setOpenEmojiForIndex(null);
                          }}
                          className={`rounded-md p-1.5 text-xl transition-all ${
                            child.emoji === emoji
                              ? "bg-primary/10 ring-2 ring-primary"
                              : "hover:bg-muted"
                          }`}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {children.length > 1 && canAddMoreChildren && (
                  <Button variant="ghost" size="sm" onClick={() => removeChild(i)}>
                    ✕
                  </Button>
                )}
              </div>
              {i === 0 && (
                <p className="text-xs text-muted-foreground">
                  Trykk på emojien for å velge avatar for barnet
                </p>
              )}
            </div>
          ))}
          <Button variant="outline" className="w-full" onClick={addChild}>
            + Legg til barn
          </Button>
          <Button onClick={handleCreateFamily} disabled={loading} className="w-full">
            {loading ? "Oppretter…" : "Kom i gang"}
          </Button>
        </CardContent>
      </Card>

      {planParam && planParam in PLAN_CONFIG && (
        <EmbeddedCheckoutDialog
          open={showCheckout}
          onOpenChange={(open) => {
            setShowCheckout(open);
          }}
          priceId={PLAN_CONFIG[planParam as PlanKey].price_id}
          familyId={checkoutFamilyId}
          planLabel={PLAN_CONFIG[planParam as PlanKey].label}
        />
      )}
    </div>
  );
}
